/**
 * Feature Guard for the Configurator (Premium Feature).
 *
 * The Configurator is a premium feature that can be enabled/disabled
 * globally via the VITE_FEATURE_CONFIGURATOR_ENABLED environment variable.
 *
 * Additionally, access is currently restricted to a whitelist of allowed
 * user email addresses for a soft-launch phase.
 *
 * Usage:
 * - In sidebar navigation: conditionally show/hide the nav item
 * - In router: guard routes so disabled feature redirects to /
 * - In components: check before rendering configurator-related UI
 *
 * To enable: set VITE_FEATURE_CONFIGURATOR_ENABLED=true in .env
 * To disable: omit the variable or set to any other value
 */

import { storageAdapter } from '../../infrastructure/storage-adapter';
import { decodeTokenPayload, authService } from '../auth/auth.service';

// ---------------------------------------------------------------------------
// Whitelist – Users allowed to access the Configurator during soft-launch
// ---------------------------------------------------------------------------

const CONFIGURATOR_ALLOWED_EMAILS: string[] = [
  's.schannak@meesenburg.de',
];

// ---------------------------------------------------------------------------
// Feature flag check
// ---------------------------------------------------------------------------

/**
 * Returns true when the Configurator premium feature is enabled.
 * Reads from the Vite environment variable at build time.
 */
export function isConfiguratorEnabled(): boolean {
  try {
    const value = import.meta.env?.VITE_FEATURE_CONFIGURATOR_ENABLED;
    return value === 'true' || value === '1';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// User-level access check
// ---------------------------------------------------------------------------

/**
 * Returns true when the current user is on the configurator whitelist.
 * During the soft-launch phase, only whitelisted emails may access the feature.
 * Checks multiple sources: JWT email claim, preferred_username, and AuthService user object.
 */
export function isConfiguratorAllowedUser(): boolean {
  try {
    // Try JWT token claims first
    const token = storageAdapter.getAccessToken();
    if (token) {
      const payload = decodeTokenPayload(token);
      if (payload?.email && CONFIGURATOR_ALLOWED_EMAILS.includes(payload.email.toLowerCase())) {
        return true;
      }
      if (payload?.preferred_username && CONFIGURATOR_ALLOWED_EMAILS.includes(payload.preferred_username.toLowerCase())) {
        return true;
      }
    }

    // Fallback: check AuthService user object (email from login/profile)
    const user = authService.getUser();
    if (user?.email && CONFIGURATOR_ALLOWED_EMAILS.includes(user.email.toLowerCase())) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Combined check: feature flag enabled AND user is on the whitelist.
 * Use this as the single entry point for configurator access decisions.
 */
export function canAccessConfigurator(): boolean {
  return isConfiguratorEnabled() && isConfiguratorAllowedUser();
}
